import React from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2, Users, X } from 'lucide-react';
import Dropdown from './Dropdown';
import SearchableSelect from './SearchableSelect';
import { formatRoleName } from '../utils/roleUtils';

/**
 * Editor for a leave type's approval chain.
 *
 * Controlled: the parent holds the ordered array and this renders it. Each step is
 * { mode, approvers: [{ approver_kind, role_id, user_id }] }: one step can list several
 * approvers ("the line manager or HR"), and its mode says whether any one of them or
 * all of them must approve. Positions are never edited here - the server numbers the
 * steps from the array order when it saves, so reordering is just moving an item.
 */

const KIND_LABELS = {
  line_manager: 'Line manager',
  role: 'Role',
  user: 'Person',
};

const newApprover = () => ({ approver_kind: 'line_manager', role_id: null, user_id: null });
const newStep = () => ({ mode: 'any', approvers: [newApprover()] });

const swap = (list, from, to) => {
  const next = [...list];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};

const LeaveApprovalChain = ({ steps = [], onChange, roles = [], users = [] }) => {
  const updateStep = (index, patch) =>
    onChange(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));

  const updateApprover = (index, entry, patch) =>
    updateStep(index, {
      approvers: steps[index].approvers.map((approver, j) =>
        j === entry ? { ...approver, ...patch } : approver
      ),
    });

  // Changing the kind drops the reference the old kind carried, or the server's
  // CHECK constraints reject an entry that names both a role and a person.
  const changeKind = (index, entry, approver_kind) =>
    updateApprover(index, entry, { approver_kind, role_id: null, user_id: null });

  // A second approver defaults to a role: the line manager is already the first one
  // on nearly every step, and the same approver twice is refused on save.
  const addApprover = (index) =>
    updateStep(index, {
      approvers: [
        ...steps[index].approvers,
        { approver_kind: 'role', role_id: null, user_id: null },
      ],
    });

  const removeApprover = (index, entry) =>
    updateStep(index, { approvers: steps[index].approvers.filter((a, j) => j !== entry) });

  const remove = (index) => onChange(steps.filter((step, i) => i !== index));

  const roleOptions = roles.map((role) => ({ value: role.id, label: formatRoleName(role.name) }));
  const userOptions = users.map((user) => ({ value: user.id, label: user.name }));

  return (
    <div data-testid="leave-chain" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center">
            <Users className="h-4 w-4 mr-2 text-primary-600" />
            Approval chain
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Requests move through these steps in order.
          </p>
        </div>
        <button
          type="button"
          data-testid="leave-chain-add"
          onClick={() => onChange([...steps, newStep()])}
          className="shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Add step
        </button>
      </div>

      {steps.length === 0 && (
        <p
          data-testid="leave-chain-empty"
          className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-3"
        >
          Anyone who can approve leaves
        </p>
      )}

      {steps.map((step, index) => {
        const approvers = step.approvers || [];
        const several = approvers.length > 1;
        const hasManager = approvers.some((a) => a.approver_kind === 'line_manager');
        return (
          <div
            key={index}
            data-testid={`leave-chain-step-${index}`}
            className="border border-gray-200 rounded-md px-3 py-3 space-y-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Step {index + 1}</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid={`leave-chain-up-${index}`}
                  onClick={() => onChange(swap(steps, index, index - 1))}
                  disabled={index === 0}
                  aria-label={`Move step ${index + 1} up`}
                  className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  data-testid={`leave-chain-down-${index}`}
                  onClick={() => onChange(swap(steps, index, index + 1))}
                  disabled={index === steps.length - 1}
                  aria-label={`Move step ${index + 1} down`}
                  className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  data-testid={`leave-chain-remove-${index}`}
                  onClick={() => remove(index)}
                  aria-label={`Remove step ${index + 1}`}
                  className="p-1 text-danger-600 hover:text-danger-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <span className="block text-xs font-medium text-gray-700">Approver</span>
              {approvers.map((approver, entry) => (
                <React.Fragment key={entry}>
                  {entry > 0 && (
                    <p
                      data-testid={`leave-chain-joiner-${index}-${entry}`}
                      className="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                      {step.mode === 'all' ? 'and' : 'or'}
                    </p>
                  )}
                  <div
                    data-testid={`leave-chain-approver-${index}-${entry}`}
                    className="flex items-start gap-2"
                  >
                    <div className="grid flex-1 min-w-0 grid-cols-1 md:grid-cols-2 gap-2">
                      <Dropdown
                        data-testid={`leave-chain-kind-${index}-${entry}`}
                        aria-label={`Approver ${entry + 1} of step ${index + 1}`}
                        value={approver.approver_kind}
                        onChange={(e) => changeKind(index, entry, e.target.value)}
                      >
                        {Object.entries(KIND_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </Dropdown>

                      {approver.approver_kind === 'role' && (
                        <SearchableSelect
                          data-testid={`leave-chain-role-${index}-${entry}`}
                          value={approver.role_id ?? ''}
                          onChange={(value) =>
                            updateApprover(index, entry, { role_id: value === '' ? null : Number(value) })
                          }
                          options={roleOptions}
                          placeholder="Choose a role…"
                        />
                      )}

                      {approver.approver_kind === 'user' && (
                        <SearchableSelect
                          data-testid={`leave-chain-user-${index}-${entry}`}
                          value={approver.user_id ?? ''}
                          onChange={(value) =>
                            updateApprover(index, entry, { user_id: value === '' ? null : Number(value) })
                          }
                          options={userOptions}
                          placeholder="Choose a person…"
                        />
                      )}
                    </div>
                    {several && (
                      <button
                        type="button"
                        data-testid={`leave-chain-remove-approver-${index}-${entry}`}
                        onClick={() => removeApprover(index, entry)}
                        aria-label={`Remove approver ${entry + 1} of step ${index + 1}`}
                        className="mt-2 p-1 text-gray-400 hover:text-danger-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </React.Fragment>
              ))}
              <button
                type="button"
                data-testid={`leave-chain-add-approver-${index}`}
                onClick={() => addApprover(index)}
                className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-800"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add another approver
              </button>
            </div>

            <div className="md:w-1/2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Needs</label>
              <Dropdown
                data-testid={`leave-chain-mode-${index}`}
                value={step.mode}
                onChange={(e) => updateStep(index, { mode: e.target.value })}
              >
                <option value="any">Any one of them</option>
                <option value="all">All of them</option>
              </Dropdown>
            </div>

            {hasManager && (
              <p className="text-xs text-gray-500">
                Line manager is the requester's manager in the org chart. A requester with
                no manager {several ? 'leaves it to the other approvers' : 'skips this step'}.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LeaveApprovalChain;
